import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React from "react";
import { useTranslation } from "react-i18next";

interface SelectionWithCategory {
  key: string;
  category: "activities" | "locations" | "days";
  translationKey: string;
}

interface SelectedItemPillsProps {
  selectedItems: string[];
  translationKey: string;
  onRemove: (key: string) => void;
  onClear: () => void;
  selectionsWithCategory?: SelectionWithCategory[];
}

const SelectedItemPills: React.FC<SelectedItemPillsProps> = ({
  selectedItems,
  translationKey,
  onRemove,
  onClear,
  selectionsWithCategory
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width:600px)");
  const maxVisiblePills = isMobile ? 1 : 1; // Show "X seleccionadas" from 2 selections onwards

  if (selectedItems.length === 0) return null;

  // Get category prefix/icon for each item
  const getCategoryInfo = (itemKey: string) => {
    if (selectionsWithCategory) {
      const selection = selectionsWithCategory.find((s) => s.key === itemKey);
      if (selection) {
        const categoryLabels: Record<
          "activities" | "locations" | "days",
          string
        > = {
          activities: "🎭",
          locations: "📍",
          days: "📅"
        };
        return {
          prefix: categoryLabels[selection.category],
          translationKey: selection.translationKey
        };
      }
    }
    return { prefix: "", translationKey };
  };

  if (selectedItems.length <= maxVisiblePills) {
    return (
      <>
        {selectedItems.map((itemKey) => {
          const { prefix, translationKey: itemTranslationKey } =
            getCategoryInfo(itemKey);
          return (
            <Box
              key={itemKey}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                bgcolor: theme.palette.customColors.chipBg,
                px: 2,
                py: 1,
                borderRadius: 3,
                fontSize: 14,
                width: isMobile && selectedItems.length === 1 ? "100%" : "auto",
                flex: isMobile && selectedItems.length === 1 ? 1 : "none",
                color: "#000000" // Ensure parent has correct color
              }}
            >
              <Box
                component="span"
                style={{
                  color: "#000000"
                }}
                sx={{
                  fontFamily: theme.typography.fontFamily,
                  fontSize: 14,
                  fontWeight: 325,
                  lineHeight: "14px",
                  letterSpacing: "0%",
                  color: "#000000 !important",
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }}
              >
                {prefix
                  ? `${prefix} ${t(`${itemTranslationKey}.${itemKey}`)}`
                  : t(`${itemTranslationKey}.${itemKey}`)}
              </Box>
              <IconButton
                size="small"
                onClick={() => onRemove(itemKey)}
                sx={{
                  p: 0,
                  minWidth: 20,
                  width: 20,
                  height: 20,
                  flexShrink: 0
                }}
              >
                <CloseIcon
                  sx={{ fontSize: 16, color: theme.palette.text.secondary }}
                />
              </IconButton>
            </Box>
          );
        })}
      </>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        bgcolor: theme.palette.customColors.chipBg,
        px: 2,
        py: 1,
        borderRadius: 3,
        fontSize: 14,
        width: isMobile ? "100%" : "auto",
        flex: isMobile ? 1 : "none",
        color: "#000000" // Ensure parent has correct color
      }}
    >
      <Box
        component="span"
        style={{
          color: "#000000"
        }}
        sx={{
          fontFamily: theme.typography.fontFamily,
          fontSize: 14,
          fontWeight: 325,
          lineHeight: "14px",
          letterSpacing: "0%",
          color: "#000000 !important",
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap"
        }}
      >
        {t(`${translationKey}.selected_count_other`, {
          count: selectedItems.length
        })}
      </Box>
      <IconButton
        size="small"
        onClick={onClear}
        sx={{ p: 0, minWidth: 20, width: 20, height: 20, flexShrink: 0 }}
      >
        <CloseIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
      </IconButton>
    </Box>
  );
};

export default SelectedItemPills;
